import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import axios from "axios";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, IconButton, Snackbar, Stack, TextField, Tooltip, Typography
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FingerprintIcon from "@mui/icons-material/Fingerprint";
import KeyIcon from "@mui/icons-material/Key";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DevicesIcon from "@mui/icons-material/Devices";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import LogoutIcon from "@mui/icons-material/Logout";
import PhoneAndroidIcon from "@mui/icons-material/PhoneAndroid";
import LaptopIcon from "@mui/icons-material/Laptop";
import PhonelinkLockIcon from "@mui/icons-material/PhonelinkLock";
import { authHeader, clearSession, isReauthCancelled, logout } from "../../authClient";
import { addPasskey } from "../../auth/passkeys";
import { NAME_MAX, nameLooksValid, saveAccountName } from "../../auth/accountName";
import BadgeIcon from "@mui/icons-material/Badge";
import { COLORS } from "../Home/constants";
import { panelSx, sectionTitleSx, sectionSubtleSx, primaryButtonSx, ghostButtonSx, dangerButtonSx, inputSx } from "../Home/styles";
import { ConfirmDialog } from "../Home/ConfirmDialog";
import { SecurityBanners } from "../Home/SecurityBanners";
import { RecoveryCodeView } from "../auth/RecoveryCodeView";

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Security
//
// Passkeys, the recovery code, the alert email, devices & sessions, and account
// deletion. Anything sensitive asks the server, and the server answers
// 403 reauth_required when a passkey check is due — authClient then shows the
// "Confirm it's you" dialog and retries, so this page never has to.
// During a recovery's 24 hours the server refuses some of these (and says so).
// ─────────────────────────────────────────────────────────────────────────────

const cfg = (extra = {}) => ({ headers: authHeader(), ...extra });
const day = (d) => (d ? new Date(d).toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : "—");

function ago(d) {
  if (!d) return "never";
  const mins = Math.round((Date.now() - new Date(d).getTime()) / 60000);
  if (mins < 2) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const h = Math.round(mins / 60);
  if (h < 36) return `${h} h ago`;
  return day(d);
}

const errorText = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const CLIENT_LABEL = { web: "Web", android: "Android app", webview: "Studio in the app" };

function Section({ icon, title, subtitle, action, children }) {
  return (
    <Box component="section" sx={panelSx}>
      <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 2, flexWrap: "wrap", rowGap: 1.5 }}>
        <Stack direction="row" spacing={1.5} alignItems="center" minWidth={0}>
          <Box sx={{ width: 36, height: 36, borderRadius: "10px", flexShrink: 0, bgcolor: "rgba(43,189,175,0.12)",
            border: "1px solid rgba(43,189,175,0.35)", display: "flex", alignItems: "center", justifyContent: "center", color: COLORS.cyan }}>
            {icon}
          </Box>
          <Box minWidth={0}>
            <Typography component="h2" sx={sectionTitleSx}>{title}</Typography>
            {subtitle && <Typography sx={sectionSubtleSx}>{subtitle}</Typography>}
          </Box>
        </Stack>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

const rowSx = {
  display: "flex", alignItems: "center", gap: 1.5, p: 1.5, borderRadius: "14px",
  border: `1px solid ${COLORS.border}`, bgcolor: COLORS.panelSoft, flexWrap: { xs: "wrap", sm: "nowrap" }
};
const chipSx = (color) => ({ height: 22, fontSize: 11.5, fontWeight: 700, color, bgcolor: "transparent", border: `1px solid ${color}` });
const iconBtnSx = { width: 36, height: 36, color: COLORS.muted, border: `1px solid ${COLORS.border}`, borderRadius: "10px",
  "&:hover": { color: COLORS.text, borderColor: COLORS.borderStrong } };

export default function SecuritySettings() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [passkeys, setPasskeys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(false);
  const [codeShown, setCodeShown] = useState(null);       // { code, title, intro }
  const [confirm, setConfirm] = useState(null);           // { title, body, label, run }
  const [rename, setRename] = useState(null);             // { id, name }
  const [deleteOpen, setDeleteOpen] = useState(false);

  const say = useCallback((text, kind = "info") => setToast({ text, kind }), []);

  // Same as the dashboard: opts this page out of App.css's old ≤1024px rules
  // (every Stack forced into a column, every button full width).
  useLayoutEffect(() => {
    document.body.classList.add("fn-studio");
    return () => document.body.classList.remove("fn-studio");
  }, []);

  const load = useCallback(async () => {
    const results = await Promise.allSettled([
      axios.get("/api/me", cfg({ skipReauth: true })),
      axios.get("/api/auth/passkeys", cfg({ skipReauth: true })),
      axios.get("/api/auth/sessions", cfg({ skipReauth: true })),
      axios.get("/api/me/email", cfg({ skipReauth: true }))
    ]);
    const [m, p, s, e] = results.map((r) => (r.status === "fulfilled" ? r.value.data : null));
    if (m) setMe(m);
    if (p) setPasskeys(p.passkeys || []);
    if (s) setSessions(s.sessions || []);
    if (e) setEmail(e);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Every action: run, report, reload. A cancelled confirm dialog says nothing.
  const act = async (fn, okText) => {
    setBusy(true);
    try {
      const result = await fn();
      if (okText) say(okText, "success");
      await load();
      return result;
    } catch (err) {
      if (!isReauthCancelled(err) && !err?.cancelled) say(errorText(err, "That didn't work. Please try again."), "error");
      return null;
    } finally { setBusy(false); setConfirm(null); }
  };

  // ── Passkeys ─────────────────────────────────────────────────────────────
  const onAddPasskey = () => act(async () => {
    const data = await addPasskey();
    if (data.recoveryCode) {
      setCodeShown({ code: data.recoveryCode, title: "Save your recovery code",
        intro: "This is your account's first recovery code. If you ever lose your devices, it gets you back in. It's shown only now." });
    }
    return data;
  }, "Passkey added");

  const onRemovePasskey = (p) => setConfirm({
    title: `Remove “${p.name}”?`,
    body: "You won't be able to sign in with it any more, and devices that signed in with it are signed out.",
    label: "Remove passkey",
    run: () => act(() => axios.delete(`/api/auth/passkeys/${p.id}`, cfg()), "Passkey removed")
  });

  const saveRename = () => {
    const { id, name } = rename;
    setRename(null);
    act(() => axios.patch(`/api/auth/passkeys/${id}`, { name }, cfg()), "Renamed");
  };

  // ── Recovery code ────────────────────────────────────────────────────────
  const onNewCode = () => setConfirm({
    title: "Create a new recovery code?",
    body: "Your current recovery code stops working immediately. Make sure you can save the new one now.",
    label: "Create new code",
    danger: false,
    run: () => act(async () => {
      const { data } = await axios.post("/api/auth/recovery-code/regenerate", {}, cfg());
      setCodeShown({ code: data.recoveryCode, title: "Your new recovery code",
        intro: "Your previous code no longer works. This one is shown only now." });
    })
  });

  // ── Sessions ─────────────────────────────────────────────────────────────
  const onRevoke = (s) => setConfirm({
    title: s.current ? "Sign out of this device?" : `Sign out “${s.deviceName}”?`,
    body: s.current ? "You'll go back to the sign-in page." : "That device will have to sign in again.",
    label: "Sign out",
    run: async () => {
      const ok = await act(() => axios.delete(`/api/auth/sessions/${s.id}`, cfg()), s.current ? null : "Device signed out");
      if (ok && s.current) { clearSession(); navigate("/login", { replace: true }); }
    }
  });

  const onSignOutEverywhere = () => setConfirm({
    title: "Sign out everywhere?",
    body: "Every device, including this one, has to sign in again. Use this if you think someone else is signed in.",
    label: "Sign out everywhere",
    run: async () => {
      const ok = await act(() => axios.post("/api/auth/sessions/revoke-all", {}, cfg()));
      if (ok) { clearSession(); navigate("/login", { replace: true }); }
    }
  });

  const passkeyAccount = me?.provider === "passkey";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: COLORS.bg, color: COLORS.text, fontFamily: COLORS.fontSans,
      px: { xs: 2, md: 4 }, py: { xs: 2, md: 4 } }}>
      <Box sx={{ maxWidth: 760, mx: "auto" }}>
        <Button component={RouterLink} to="/home" startIcon={<ArrowBackIcon />}
          sx={{ color: COLORS.muted, textTransform: "none", fontWeight: 600, mb: 1.5, px: 1 }}>
          Back to the studio
        </Button>
        <Typography component="h1" sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 800, fontSize: { xs: 30, md: 38 }, lineHeight: 1.1 }}>
          Security
        </Typography>
        <Typography sx={{ color: COLORS.muted, fontSize: 15, mt: 0.75, mb: 3 }}>
          {passkeyAccount
            ? "Your Fennira account is your passkeys. No email or password is needed to sign in."
            : me?.email ? `Signed in as ${me.email}.` : " "}
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress sx={{ color: COLORS.cyan }} /></Box>
        ) : (
          <Stack spacing={2.5}>
            <SecurityBanners showMessage={say} onAccountChanged={load} />

            {/* ── Account name ────────────────────────────────────────────── */}
            <AccountNameSection name={me?.displayName || ""} busy={busy} act={act} />

            {/* ── Passkeys ─────────────────────────────────────────────────── */}
            <Section icon={<FingerprintIcon fontSize="small" />} title="Passkeys"
              subtitle={passkeys.length ? `${passkeys.length} passkey${passkeys.length === 1 ? "" : "s"} can sign in to this account` : "Sign in with your fingerprint, face or screen lock"}
              action={<Button onClick={onAddPasskey} disabled={busy} startIcon={<FingerprintIcon />} sx={primaryButtonSx}>Add a passkey</Button>}>
              <Stack spacing={1}>
                {passkeys.length === 0 && (
                  <Typography sx={{ color: COLORS.muted, fontSize: 14 }}>
                    No passkey yet. Add one to stop using your password.
                  </Typography>
                )}
                {passkeys.map((p) => (
                  <Box key={p.id} sx={rowSx}>
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: .5 }}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>{p.name}</Typography>
                        {p.usedForThisSession && <Chip label="This device" size="small" sx={chipSx(COLORS.green)} />}
                        {p.addedDuringRecovery && <Chip label="Added during a recovery" size="small" sx={chipSx(COLORS.amber)} />}
                        {p.backedUp && <Chip label="Synced" size="small" sx={chipSx(COLORS.muted)} />}
                      </Stack>
                      <Typography sx={{ color: COLORS.muted, fontSize: 12.5, mt: .25 }}>
                        Added {day(p.createdAt)} · last used {ago(p.lastUsedAt)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="Rename">
                        <IconButton aria-label={`Rename ${p.name}`} onClick={() => setRename({ id: p.id, name: p.name })} disabled={busy} sx={iconBtnSx}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={passkeys.length <= 1 ? "Add another passkey before removing this one" : "Remove"}>
                        <span>
                          <IconButton aria-label={`Remove ${p.name}`} onClick={() => onRemovePasskey(p)}
                            disabled={busy || passkeys.length <= 1} sx={iconBtnSx}>
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            </Section>

            {/* ── Recovery code ───────────────────────────────────────────── */}
            {(passkeys.length > 0 || me?.hasRecoveryCode) && (
              <Section icon={<KeyIcon fontSize="small" />} title="Recovery code"
                subtitle={me?.hasRecoveryCode ? "Saved when you created it. Fennira can't show it again." : "You don't have one right now."}
                action={<Button onClick={onNewCode} disabled={busy} sx={me?.hasRecoveryCode ? ghostButtonSx : primaryButtonSx}>
                  {me?.hasRecoveryCode ? "Create a new code" : "Create a code"}
                </Button>}>
                <Typography sx={{ color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55 }}>
                  If you lose every passkey, the recovery code gets you back in on a new device. It works once. When
                  it's used, your other devices are alerted and stream keys stay locked for 24 hours.
                </Typography>
              </Section>
            )}

            {/* ── Alert email ─────────────────────────────────────────────── */}
            <AlertEmailSection email={email} busy={busy} act={act} say={say} />

            {/* ── Devices & sessions ──────────────────────────────────────── */}
            <Section icon={<DevicesIcon fontSize="small" />} title="Devices & sessions"
              subtitle="Where your account is signed in. IP addresses are kept for 7 days at most."
              action={<Button onClick={onSignOutEverywhere} disabled={busy} startIcon={<LogoutIcon />} sx={ghostButtonSx}>Sign out everywhere</Button>}>
              <Stack spacing={1}>
                {sessions.map((s) => (
                  <Box key={s.id} sx={rowSx}>
                    <Box sx={{ color: COLORS.muted, display: "flex" }}>
                      {s.client === "web" ? <LaptopIcon /> : <PhoneAndroidIcon />}
                    </Box>
                    <Box sx={{ flex: "1 1 220px", minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", rowGap: .5 }}>
                        <Typography noWrap sx={{ fontWeight: 700, fontSize: 15 }}>{s.deviceName || "Unknown device"}</Typography>
                        {s.current && <Chip label="This device" size="small" sx={chipSx(COLORS.green)} />}
                        {s.restrictedUntil && <Chip label="Recovery, limited" size="small" sx={chipSx(COLORS.amber)} />}
                      </Stack>
                      <Typography sx={{ color: COLORS.muted, fontSize: 12.5, mt: .25 }}>
                        {CLIENT_LABEL[s.client] || "Web"} · signed in {day(s.createdAt)}
                        {s.authMethod === "phone"
                          ? ` with a code approved from ${s.approvedFrom && s.approvedFrom !== "Unknown device" ? s.approvedFrom : "your phone"}` : ""} · active {ago(s.lastSeenAt)}
                        {s.lastIp ? ` · ${s.lastIp}` : ""}
                      </Typography>
                    </Box>
                    <Button onClick={() => onRevoke(s)} disabled={busy} sx={{ ...ghostButtonSx, height: 36 }}>
                      {s.current ? "Sign out" : "Revoke"}
                    </Button>
                  </Box>
                ))}
              </Stack>
            </Section>

            {/* ── Sign in on a computer (a code shown on its sign-in page) ── */}
            <Section icon={<PhonelinkLockIcon fontSize="small" />} title="Sign in on a computer"
              subtitle="A computer without Bluetooth shows a code on its sign-in page. Approve it here."
              action={<Button component={RouterLink} to="/link" sx={ghostButtonSx}>Enter a code</Button>} />

            {/* ── Delete ──────────────────────────────────────────────────── */}
            <Section icon={<DeleteForeverIcon fontSize="small" />} title="Delete account"
              subtitle="Permanently removes your stream keys, connected platforms, videos, passkeys and sessions."
              action={<Button onClick={() => setDeleteOpen(true)} disabled={busy} sx={dangerButtonSx}>Delete account</Button>} />
          </Stack>
        )}
      </Box>

      <ConfirmDialog
        open={!!confirm}
        onClose={() => !busy && setConfirm(null)}
        onConfirm={() => confirm?.run()}
        title={confirm?.title || ""}
        body={confirm?.body || ""}
        confirmLabel={confirm?.label || "OK"}
      />

      <DeleteAccountDialog open={deleteOpen} onClose={() => setDeleteOpen(false)}
        onDeleted={async () => { await logout(); navigate("/login", { replace: true }); }} say={say} />

      <Dialog open={!!rename} onClose={() => setRename(null)}
        PaperProps={{ sx: { width: "100%", maxWidth: 420, m: 2, p: 3, bgcolor: COLORS.raised, backgroundImage: "none",
          color: COLORS.text, border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px" } }}>
        <Typography sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 20, mb: 2 }}>Rename passkey</Typography>
        <TextField autoFocus fullWidth size="small" value={rename?.name || ""} inputProps={{ maxLength: 60 }}
          onChange={(e) => setRename((r) => ({ ...r, name: e.target.value }))}
          onKeyDown={(e) => { if (e.key === "Enter" && rename?.name.trim()) saveRename(); }} sx={inputSx} />
        <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 3 }}>
          <Button onClick={() => setRename(null)} sx={ghostButtonSx}>Cancel</Button>
          <Button onClick={saveRename} disabled={!rename?.name.trim()} sx={primaryButtonSx}>Save</Button>
        </Stack>
      </Dialog>

      <Dialog open={!!codeShown} onClose={() => {}} disableEscapeKeyDown
        PaperProps={{ sx: { width: "100%", maxWidth: 480, m: 2, p: { xs: 3, sm: 3.5 }, bgcolor: COLORS.raised,
          backgroundImage: "none", color: COLORS.text, border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px" } }}>
        {codeShown && <RecoveryCodeView tone="dark" {...codeShown} continueLabel="Done" onContinue={() => setCodeShown(null)} />}
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        {toast ? <Alert severity={toast.kind} onClose={() => setToast(null)} sx={{ borderRadius: "12px" }}>{toast.text}</Alert> : <span />}
      </Snackbar>
    </Box>
  );
}

/** The account's name: shown in the Studio and on the passkeys. No passkey check (PLAN.md D4). */
function AccountNameSection({ name, busy, act }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(name);
  useEffect(() => { if (!editing) setValue(name); }, [name, editing]);
  const save = async () => {
    if (!nameLooksValid(value)) return;
    const done = await act(() => saveAccountName(value), "Account name saved.");
    if (done) setEditing(false);
  };
  return (
    <Section icon={<BadgeIcon fontSize="small" />} title="Account name"
      subtitle="Shown in the Studio and on your passkeys, so your accounts are easy to tell apart."
      action={!editing && <Button onClick={() => setEditing(true)} disabled={busy} startIcon={<EditIcon />} sx={ghostButtonSx}>Edit</Button>}>
      {editing ? (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} alignItems={{ sm: "center" }}>
          <TextField autoFocus fullWidth size="small" value={value} onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") setEditing(false); }}
            inputProps={{ maxLength: NAME_MAX, "aria-label": "Account name" }} disabled={busy}
            sx={{ ...inputSx, "& .MuiOutlinedInput-root": { ...inputSx["& .MuiOutlinedInput-root"], fontFamily: COLORS.fontSans, fontSize: "0.95rem" } }} />
          <Stack direction="row" spacing={1}>
            <Button onClick={() => setEditing(false)} disabled={busy} sx={ghostButtonSx}>Cancel</Button>
            <Button onClick={save} disabled={busy || !nameLooksValid(value)} sx={primaryButtonSx}>Save</Button>
          </Stack>
        </Stack>
      ) : (
        <Typography sx={{ fontWeight: 700, fontSize: 17 }}>{name || "No name yet"}</Typography>
      )}
    </Section>
  );
}

function AlertEmailSection({ email, busy, act, say }) {
  const [editing, setEditing] = useState(false);
  const [address, setAddress] = useState("");
  const [pending, setPending] = useState("");
  const [otp, setOtp] = useState("");

  useEffect(() => { setPending(email?.pending || ""); }, [email]);

  const send = () => act(async () => {
    const { data } = await axios.post("/api/me/email", { address: address.trim() }, cfg());
    setPending(data.pending);
    setOtp("");
  });
  const verify = () => act(async () => {
    await axios.post("/api/me/email/verify", { code: otp }, cfg());
    setEditing(false); setPending(""); setAddress("");
  }, "Alert email confirmed");
  const remove = () => act(() => axios.delete("/api/me/email", cfg()), "Alerts by email turned off");

  const showForm = editing || pending;
  return (
    <Section icon={<MailOutlineIcon fontSize="small" />} title="Alert email"
      subtitle="Optional. Only for security alerts and receipts — never for signing in or recovery."
      action={!showForm && (
        <Stack direction="row" spacing={1}>
          {email?.address && <Button onClick={remove} disabled={busy} sx={ghostButtonSx}>Turn off</Button>}
          <Button onClick={() => setEditing(true)} disabled={busy} sx={email?.address ? ghostButtonSx : primaryButtonSx}>
            {email?.address ? "Change" : "Add an email"}
          </Button>
        </Stack>
      )}>
      {!showForm && (
        <Typography sx={{ color: COLORS.textSoft, fontSize: 14 }}>
          {email?.address
            ? <>Alerts go to <b>{email.address}</b>{email.source === "login" ? " (your sign-in email)" : ""}.</>
            : "No email: you'll see alerts in the studio only."}
        </Typography>
      )}
      {showForm && !pending && (
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
          <TextField autoFocus size="small" fullWidth type="email" placeholder="you@example.com" value={address}
            onChange={(e) => setAddress(e.target.value)} sx={inputSx} />
          <Button onClick={send} disabled={busy || !address.includes("@")} sx={{ ...primaryButtonSx, flexShrink: 0 }}>Send code</Button>
          <Button onClick={() => setEditing(false)} disabled={busy} sx={{ ...ghostButtonSx, flexShrink: 0 }}>Cancel</Button>
        </Stack>
      )}
      {pending && (
        <Box>
          <Typography sx={{ color: COLORS.textSoft, fontSize: 14, mb: 1.25 }}>
            Enter the 6-digit code we sent to <b>{pending}</b>.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <TextField autoFocus size="small" placeholder="123456" value={otp} inputProps={{ maxLength: 6, inputMode: "numeric" }}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} sx={{ ...inputSx, maxWidth: { sm: 180 } }} />
            <Button onClick={verify} disabled={busy || otp.length !== 6} sx={{ ...primaryButtonSx, flexShrink: 0 }}>Confirm</Button>
            <Button onClick={() => { setPending(""); setEditing(false); say("Not changed"); }} disabled={busy}
              sx={{ ...ghostButtonSx, flexShrink: 0 }}>Cancel</Button>
          </Stack>
        </Box>
      )}
    </Section>
  );
}

function DeleteAccountDialog({ open, onClose, onDeleted, say }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setText(""); }, [open]);

  const go = async () => {
    setBusy(true);
    try {
      await axios.delete("/api/account", cfg());
      await onDeleted();
    } catch (err) {
      if (!isReauthCancelled(err)) say(errorText(err, "Could not delete the account"), "error");
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()}
      PaperProps={{ sx: { width: "100%", maxWidth: 460, m: 2, p: 3.5, bgcolor: COLORS.raised, backgroundImage: "none",
        color: COLORS.text, border: `1px solid ${COLORS.borderStrong}`, borderRadius: "22px" } }}
      sx={{ "& .MuiBackdrop-root": { bgcolor: "rgba(8,7,24,0.72)" } }}>
      <Typography sx={{ fontFamily: COLORS.fontDisplay, fontWeight: 700, fontSize: 20 }}>Delete your account?</Typography>
      <Typography sx={{ mt: 1, color: COLORS.textSoft, fontSize: 14, lineHeight: 1.55 }}>
        This permanently deletes your stream keys, connected platforms, uploaded videos, passkeys and every session.
        It can't be undone. Type <b>DELETE</b> to confirm.
      </Typography>
      <TextField autoFocus fullWidth size="small" value={text} onChange={(e) => setText(e.target.value)}
        placeholder="DELETE" sx={{ ...inputSx, mt: 2 }} />
      <Stack direction="row" spacing={1.25} justifyContent="flex-end" sx={{ mt: 3 }}>
        <Button onClick={onClose} disabled={busy} sx={ghostButtonSx}>Keep my account</Button>
        <Button onClick={go} disabled={busy || text.trim() !== "DELETE"} sx={dangerButtonSx}>
          {busy ? "Deleting…" : "Delete account"}
        </Button>
      </Stack>
    </Dialog>
  );
}
